export const REASON_LABELS = {
  confidence_below_0_50: 'Score de fiabilité insuffisant',
  schema_invalid: 'Format candidat incompatible',
  skills_not_complete: 'Collecte des compétences incomplète',
  possible_skill_omissions: 'Compétences visibles potentiellement oubliées',
  final_values_without_evidence: "Une valeur n'est pas confirmée par LinkedIn",
  weaviate_error: 'Stockage ou vectorisation temporairement indisponible',
  extraction_failed: 'La structuration du profil a échoué',
  linkedin_profile_unavailable: 'Profil LinkedIn indisponible (page 404)',
  scraping_failed: 'La collecte LinkedIn a échoué',
  location_normalization_allowed: 'Normalisation géographique acceptée',
};

export const statusLabel = (item) =>
  item.status === 'stored' ? 'Stocké et vectorisé' : REASON_LABELS[item.reason_code] || item.reason_code || item.status || '—';

export const CANDIDATE_STEPS = [
  { id: 'load_csv', label: 'Lecture du CSV' },
  { id: 'scrape', label: 'Collecte LinkedIn' },
  { id: 'extract', label: 'Extraction & structuration' },
];

export const MISSION_STEPS = [
  { id: 'jobs_load_csv', label: 'Lecture du CSV' },
  { id: 'jobs_open', label: "Ouverture de l'offre LinkedIn" },
  { id: 'jobs_collect', label: 'Chargement et défilement' },
  { id: 'jobs_extract', label: 'Extraction et structuration' },
  { id: 'jobs_validate', label: 'Contrôle factuel' },
  { id: 'jobs_store', label: 'Stockage' },
  { id: 'jobs_wait', label: 'Attente de sécurité' },
];

export const SEARCH_MODES = [
  { value: 'vecteur', label: 'Vectoriel', description: 'Proximité de sens entre candidat et mission, même avec des mots différents.' },
  { value: 'texte', label: 'Textuel', description: 'Correspondances exactes de mots-clés : compétences, technologies, intitulés.' },
  { value: 'hybride', label: 'Hybride', description: 'Combine le sens et les mots-clés exacts pour un classement équilibré.' },
];

export const MISSION_TENANTS = [
  { value: 'official', label: 'Missions officielles', description: 'Missions internes Go & Dev déjà présentes dans le référentiel (Job/job).' },
  { value: 'sourcing', label: 'Missions sourcées', description: 'Offres collectées sur LinkedIn par le sourcing, dans un espace isolé (Job/sourcing_jobs).' },
];

export const CANDIDATE_TENANTS = [
  { value: 'official', label: 'Candidats officiels', description: 'Candidats déjà validés dans le référentiel officiel (Candidate/cv).' },
  { value: 'sourcing', label: 'Candidats sourcés', description: 'Profils collectés sur LinkedIn et validés dans l’espace sourcing (Candidate/sourcing_candidates).' },
];

// Reasons a "smart retry" can fix from the stored evidence, without a new LinkedIn collection.
export const SMART_RETRY_REASONS = ['possible_skill_omissions', 'final_values_without_evidence', 'skills_section_collection_failed', 'skills_not_complete'];

const LINKEDIN_ERROR_LABELS = new Set([
  'il y a un probleme', 'rafraichir la page', 'reessayer', 'une erreur s est produite',
  'something went wrong', 'refresh the page', 'reload the page', 'try again',
]);

const normalizeLabel = (value) =>
  String(value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();

/** Skills polluted by LinkedIn error banners: the profile must be collected again. */
export const hasLinkedinErrorNoise = (item) =>
  [...((item.payload || {}).technical_skills || []), ...((item.payload || {}).programming_languages || [])]
    .some((v) => LINKEDIN_ERROR_LABELS.has(normalizeLabel(v)));

export const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR');
};

export const candidateUrl = (item) => item.linkedin_url || (item.payload || {}).linkedin || (item.payload || {}).linkedin_url;
export const candidateName = (item) => (item.payload || {}).full_name || item.full_name || 'Nom indisponible';
export const candidateRole = (item) => (item.payload || {}).roles_held?.[0] || item.primary_role || '';
export const candidateLocation = (item) => (item.payload || {}).location || item.location || '';

export const initials = (name) =>
  String(name || '?').trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase() || '?';

const blankValue = (v) => !v || ['not found', 'null', 'none', 'n/a'].includes(String(v).trim().toLowerCase());
const clean = (v) => (blankValue(v) ? '' : String(v).trim());

/** Current employer and start year, read from the LinkedIn "Expérience" text. */
const DATE_LINE = /\b(19|20)\d{2}\b|aujourd|present|présent/i;
const CONTRACT_TYPES = ['CDI', 'CDD', 'Freelance', 'Indépendant', 'Stage', 'Alternance', 'Temps partiel', 'Intérim', 'Full-time', 'Part-time'];

// LinkedIn "Expérience" block, most recent job first:
//   <role> / <company> [· <contract>] / <start> - <end> · <duration> / ...
function currentJob(workExperience) {
  const lines = String(workExperience || '').split('\n').map((l) => l.trim()).filter(Boolean);
  const start = lines[0]?.toLowerCase().startsWith('exp') ? 1 : 0;
  const companyLine = lines[start + 1] || '';
  const companyIsDate = DATE_LINE.test(companyLine);
  const [companyPart, ...rest] = companyLine.split('·').map((s) => s.trim());
  const employer = companyIsDate ? '' : companyPart || '';
  const contract = rest.find((r) => CONTRACT_TYPES.some((t) => r.toLowerCase().startsWith(t.toLowerCase()))) || '';
  const dateLine = lines.slice(start + 1, start + 4).find((l) => DATE_LINE.test(l)) || '';
  const yearMatch = dateLine.match(/\b(19|20)\d{2}\b/);
  return {
    role: lines[start] || '',
    employer,
    contract,
    startYear: yearMatch ? yearMatch[0] : '',
    current: /aujourd|present|présent/i.test(dateLine),
  };
}

const SENIORITY = [
  { min: 15, label: 'Expert' },
  { min: 8, label: 'Senior' },
  { min: 4, label: 'Confirmé' },
  { min: 1, label: 'Junior' },
  { min: 0, label: 'Débutant' },
];

/**
 * At-a-glance summary of a sourced profile: experience, seniority, main stack, current job.
 * `text` is the one-line version sent to the CRM "Sachez que" field.
 */
export function profileSummary(item) {
  const p = item.payload || {};
  const years = typeof p.years_of_experience === 'number' ? Math.round(p.years_of_experience) : null;
  const seniority = years === null ? null : SENIORITY.find((s) => years >= s.min).label;
  const stack = [...(p.technical_skills || []), ...(p.programming_languages || [])]
    .filter((s) => typeof s === 'string' && !blankValue(s))
    .filter((s, i, all) => all.findIndex((x) => x.toLowerCase() === s.toLowerCase()) === i)
    .slice(0, 4);
  const job = currentJob(p.work_experience);
  const role = clean(job.role || (p.roles_held || [])[0]).replace(/\s*\(.*\)\s*$/, '');
  const education = clean(p.education_level);
  const industries = (p.industry_primary_industries || []).filter((s) => typeof s === 'string').slice(0, 2);

  const jobText = job.employer
    ? `chez ${job.employer}${job.current && job.startYear ? ` depuis ${job.startYear}` : ''}${job.contract ? ` (${job.contract})` : ''}`
    : '';
  const text = [
    years !== null ? `${years} an${years > 1 ? 's' : ''} d'expérience` : null,
    seniority,
    stack.length ? stack.slice(0, 3).join('/') : null,
    [role, jobText].filter(Boolean).join(' ') || null,
    education || null,
  ].filter(Boolean).join(' · ');

  return { years, seniority, stack, role, employer: job.employer, since: job.current ? job.startYear : '', contract: job.contract, education, industries, text };
}

/** Pre-filled CRM form from a sourced candidate. */
export function toCrmForm(item) {
  const p = item.payload || {};
  const parts = clean(p.full_name || item.full_name).split(/\s+/).filter(Boolean);
  const job = currentJob(p.work_experience);
  return {
    linkedin: clean(candidateUrl(item)),
    prenom: parts[0] || '',
    nom: parts.slice(1).join(' '),
    fonction: clean((p.roles_held || [])[0] || item.primary_role).replace(/\s*\(.*\)\s*$/, ''),
    employeur: job.employer,
    localisation: clean(p.location || item.location),
    email: clean(p.email),
    telephone: clean(p.phone),
    ecole: '',
    date_debut: job.startYear,
    competences: [...(p.technical_skills || []), ...(p.programming_languages || [])].filter((s) => typeof s === 'string').join(', '),
    sachez_que: profileSummary(item).text,
  };
}

// Sourced candidates already added to this CRM: { candidate_uuid: crmCandidateId }.
const CRM_LINKS_KEY = 'sourcing_crm_links';
export const readCrmLinks = () => {
  try { return JSON.parse(localStorage.getItem(CRM_LINKS_KEY) || '{}'); } catch (e) { return {}; }
};
export const saveCrmLink = (uuid, crmId) => {
  try { localStorage.setItem(CRM_LINKS_KEY, JSON.stringify({ ...readCrmLinks(), [uuid]: crmId })); } catch (e) { /* ignore */ }
};
